import { DestroyRef, inject, Service, signal } from '@angular/core';

type ThemeChoice = 'light' | 'dark';

/** Shared with the pre-paint script in index.html, which applies the same rule before Angular loads. */
export const THEME_STORAGE_KEY = 'hub-escolar.theme';
/** Written by the previous service, which stored 'light' on every start whatever the user wanted. */
const LEGACY_STORAGE_KEY = 'theme';
const DARK_CLASS = 'app-dark';
const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * Light or dark, as the `app-dark` class on <html> (PrimeNG's darkModeSelector and Tailwind's dark variant).
 * The system decides, live, until the user presses the toggle; from then on the stored choice wins, in every
 * open tab. The class stays on <html> because that is where PrimeNG declares its variables.
 */
@Service()
export class ThemeService {
  private readonly root = document.documentElement;
  /** Null where the browser cannot answer the query (jsdom): the system then reads as light. */
  private readonly system = typeof window.matchMedia === 'function' ? window.matchMedia(DARK_QUERY) : null;
  /** Null until the user presses the toggle, here or in another tab. */
  private choice: ThemeChoice | null = readStoredChoice();
  private readonly darkMode = signal(this.resolve());

  readonly isDark = this.darkMode.asReadonly();

  constructor() {
    // Normally a no-op: the pre-paint script in index.html set the class by the same rule on the same storage.
    this.root.classList.toggle(DARK_CLASS, this.darkMode());

    const onSystemChange = () => {
      if (this.choice === null) {
        this.apply();
      }
    };
    // Fired only in the other tabs: a press there is a choice here too, as it would be after a reload.
    const onStorage = (event: StorageEvent) => {
      if (event.key === THEME_STORAGE_KEY) {
        this.choice = isThemeChoice(event.newValue) ? event.newValue : null;
        this.apply();
      }
    };
    this.system?.addEventListener('change', onSystemChange);
    window.addEventListener('storage', onStorage);
    // A root service lives as long as the page; this matters only where an injector is torn down, as in tests.
    inject(DestroyRef).onDestroy(() => {
      this.system?.removeEventListener('change', onSystemChange);
      window.removeEventListener('storage', onStorage);
    });
  }

  /** Stores the opposite of what is on screen, even when it matches the system: a press is a choice. */
  toggle(): void {
    this.choice = this.darkMode() ? 'light' : 'dark';
    storeChoice(this.choice);
    this.apply();
  }

  private resolve(): boolean {
    return this.choice === null ? (this.system?.matches ?? false) : this.choice === 'dark';
  }

  private apply(): void {
    const isDark = this.resolve();
    this.root.classList.toggle(DARK_CLASS, isDark);
    this.darkMode.set(isDark);
  }
}

function isThemeChoice(value: string | null): value is ThemeChoice {
  return value === 'light' || value === 'dark';
}

function readStoredChoice(): ThemeChoice | null {
  let stored: string | null;
  let legacy: string | null;
  try {
    stored = localStorage.getItem(THEME_STORAGE_KEY);
    legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
  } catch {
    // Storage blocked: follow the system, as index.html does.
    return null;
  }

  if (isThemeChoice(stored)) {
    return stored;
  }
  if (legacy === null) {
    return null;
  }

  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // Retried on the next start.
  }
  // The old service wrote 'light' on every start, so only 'dark' was ever a real choice.
  if (legacy !== 'dark') {
    return null;
  }
  storeChoice('dark');
  return 'dark';
}

function storeChoice(choice: ThemeChoice): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, choice);
  } catch {
    // Storage blocked: the choice lasts for this visit.
  }
}
